import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { createMvpDraft, type MvpEntryMode } from "@/hooks/use-mvp-persistence";
import { trackEvent } from "@/lib/posthog";
import { BuilderWordmark } from "@/components/BuilderWordmark";
import { EntryModeCards } from "@/components/mvp/EntryModeCards";
import { OfferDocumentModeCards } from "@/components/mvp/OfferDocumentModeCards";
import type { TemplateType } from "@/components/offer/offerDocumentTypes";
import styles from "@/pages/MvpNewDraftRedirect.module.css";

/** Wybór formatu i trybu wprowadzania, utworzenie draftu i przejście do `/mvp/:id`. */
export default function MvpNewDraftRedirect() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedType, setSelectedType] = useState<TemplateType | null>(null);
  const [entryModeStepVisible, setEntryModeStepVisible] = useState(false);
  const [selectedEntryMode, setSelectedEntryMode] = useState<MvpEntryMode | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateDraft = async (entryMode: MvpEntryMode) => {
    if (!user || !selectedType || submitting) return;
    setSelectedEntryMode(entryMode);
    setSubmitting(true);
    setError(null);
    trackEvent("quote_started", { entryMode });

    try {
      const { id } = await createMvpDraft(user.id, selectedType, entryMode);
      navigate(
        `/mvp/${id}`,
        entryMode === "packages"
          ? { replace: true, state: { forceWorkshopStep1: true } }
          : { replace: true },
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Nie udało się utworzyć szkicu.");
      setSelectedEntryMode(null);
      setSubmitting(false);
    }
  };

  const handleFormatNext = () => {
    if (!selectedType) return;
    setError(null);
    setEntryModeStepVisible(true);
  };

  return (
    <main className={styles.page}>
      <div className={entryModeStepVisible ? styles.innerNarrow : styles.inner}>
        <div className={styles.topbar}>
          {entryModeStepVisible ? (
            <button
              className={styles.backButton}
              type="button"
              onClick={() => {
                setEntryModeStepVisible(false);
                setSelectedEntryMode(null);
                setError(null);
              }}
              disabled={submitting}
            >
              ← Zmień format
            </button>
          ) : (
            <BuilderWordmark />
          )}
          {entryModeStepVisible ? (
            <BuilderWordmark />
          ) : (
            <Link className={styles.backLink} to="/dashboard/oferty">
              Wróć do ofert
            </Link>
          )}
        </div>

        {entryModeStepVisible ? (
          <>
            <header className={styles.header}>
              <p className={styles.eyebrow}>Nowa oferta</p>
              <h1 className={styles.title}>Jak chcesz wprowadzić pozycje?</h1>
              <p className={styles.description}>
                Wybierz szybki start z pakietami albo zacznij od pustej tabeli kosztorysu.
              </p>
            </header>

            <EntryModeCards
              value={selectedEntryMode}
              submitting={submitting}
              onChange={(entryMode) => void handleCreateDraft(entryMode)}
            />

            <div className={styles.actions}>{error ? <p className={styles.error}>{error}</p> : null}</div>
          </>
        ) : (
          <>
            <header className={styles.header}>
              <p className={styles.eyebrow}>Nowa oferta</p>
              <h1 className={styles.title}>Wybierz format oferty dla klienta</h1>
              <p className={styles.description}>
                Format decyduje o tym, co zobaczy klient w dokumencie. Możesz go zmienić później.
              </p>
            </header>

            <OfferDocumentModeCards value={selectedType} onChange={setSelectedType} />

            <div className={styles.actions}>
              {error ? <p className={styles.error}>{error}</p> : null}
              <button
                className={styles.next}
                type="button"
                onClick={handleFormatNext}
                disabled={!selectedType || submitting}
              >
                Wybierz →
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
